/**
 * EstimateBadge - Compact display of a Work Order or Inspection's
 * estimate, with an optional "Approved" checkmark when the estimate
 * has been signed off.
 *
 * Renders three formats based on which fields are set:
 *   1. Single point     — `$1,250.00`
 *   2. Range only       — `$800 – $1,500`
 *   3. Single + range   — `$1,250.00 (range $800 – $1,500)`
 *   4. None             — returns null (parent should hide the badge)
 *
 * The component is presentation-only — formatting + approval display.
 * Approve/revoke actions live in the EstimateApprovalControl organism.
 *
 * Used by:
 *   - WorkOrderDetailPage / InspectionDetailPage details panels
 *   - Future dashboard cards that surface estimates inline
 *
 * @example
 * <EstimateBadge cost={1250.00} approved />
 * <EstimateBadge min={800} max={1500} />
 */
import React from 'react';
import { formatCurrencyOrNull } from '../../../utils/formatCurrency';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Props for EstimateBadge.
 *
 * All numeric values are optional; the component renders whichever
 * combination of fields is present.
 *
 * @property cost      Single-point estimate (renders as the primary value)
 * @property min       Lower bound of an optional range
 * @property max       Upper bound of an optional range
 * @property approved  When true, renders a green check + "Approved" label
 * @property size      Visual scale; defaults to 'sm'
 * @property className Optional extra classes (merged after defaults)
 */
export interface EstimateBadgeProps {
    cost?: number | string | null;
    min?: number | string | null;
    max?: number | string | null;
    approved?: boolean;
    size?: 'sm' | 'md';
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const SIZE_CLASSES: Record<NonNullable<EstimateBadgeProps['size']>, string> = {
    sm: 'text-12px px-8px py-2px gap-4px',
    md: 'text-14px px-10px py-4px gap-6px',
};

export const EstimateBadge: React.FC<EstimateBadgeProps> = ({
    cost,
    min,
    max,
    approved = false,
    size = 'sm',
    className,
}) => {
    const costStr = formatCurrencyOrNull(cost);
    const minStr = formatCurrencyOrNull(min);
    const maxStr = formatCurrencyOrNull(max);

    /* Build the display string from whichever combination of fields
     * is present. No fields → null render so callers can keep their
     * markup clean without an explicit guard. */
    let main: string | null = null;
    let subtext: string | null = null;
    if (costStr) {
        main = costStr;
        if (minStr && maxStr) {
            subtext = `range ${minStr} – ${maxStr}`;
        }
    } else if (minStr && maxStr) {
        main = `${minStr} – ${maxStr}`;
    } else if (minStr || maxStr) {
        // Only one bound present — show it as a partial range.
        main = `${minStr ?? '—'} – ${maxStr ?? '—'}`;
    }

    if (!main) return null;

    return (
        <span
            data-ref="atom-estimate-badge"
            className={[
                `estimate-badge${approved ? ' estimate-badge--approved' : ''}`,
                'inline-flex items-center font-sans font-medium rounded border',
                approved
                    ? 'bg-green-50 text-green-800 border-green-200'
                    : 'bg-brand-surface text-brand-text border-brand-border',
                SIZE_CLASSES[size],
                className ?? '',
            ].join(' ')}
            aria-label={approved ? `Approved estimate ${main}` : `Estimate ${main}`}
        >
            {/* Optional approval check mark — only shown when approved=true */}
            {approved && (
                <svg className="estimate-badge__check w-12px h-12px text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
            )}
            <span className="estimate-badge__value">{main}</span>
            {subtext && <span className="estimate-badge__subtext text-brand-text-muted">({subtext})</span>}
            {approved && <span className="estimate-badge__label text-green-700 text-11px uppercase tracking-wide">Approved</span>}
        </span>
    );
};

export default EstimateBadge;
